import React, { useState } from 'react';

/**
 * Image component that supports both Firebase Storage URLs and legacy Cloudinary URLs
 * Firebase Storage URLs are used directly, Cloudinary URLs are supported for backward compatibility
 */
const CloudinaryImage = ({ 
  publicId, 
  alt = '', 
  className = '', 
  width, 
  height, 
  crop = 'fill',
  quality = 'auto',
  format = 'auto',
  type = 'optimized', // 'optimized', 'thumbnail', 'avatar', 'custom' (legacy, not used for Firebase)
  ...props 
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  if (!publicId) {
    return (
      <div className={`relative bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center ${className}`}>
        <div className="text-center">
          <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-2">
            <span className="text-gray-500 text-xl">🏠</span>
          </div>
          <span className="text-gray-500 text-xs">Geen foto</span>
        </div>
      </div>
    );
  }

  if (imageError) {
    return (
      <div className={`relative bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center ${className}`}>
        <div className="text-center">
          <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-2">
            <span className="text-gray-500 text-xl">🏠</span>
          </div>
          <span className="text-gray-500 text-xs">Foto niet gevonden</span>
        </div>
      </div>
    );
  }

  let imageUrl;

  // Check if publicId is already a full URL (Firebase Storage or Cloudinary)
  if (publicId && (publicId.includes('http') || publicId.includes('firebasestorage') || publicId.includes('cloudinary.com'))) {
    // If it's already a full URL (Firebase Storage or Cloudinary), use it directly
    imageUrl = publicId;
  } else {
    // Legacy Cloudinary public ID - build Cloudinary URL for backward compatibility
    // Import dynamically to avoid breaking if cloudinary config is removed
    try {
      const { buildCloudinaryUrl, getOptimizedImageUrl, getThumbnailUrl, getAvatarUrl } = require('../config/cloudinary');
      switch (type) {
        case 'thumbnail':
          imageUrl = getThumbnailUrl(publicId, width || 200);
          break;
        case 'avatar':
          imageUrl = getAvatarUrl(publicId, width || 100);
          break;
        case 'custom':
          imageUrl = buildCloudinaryUrl(publicId, { width, height, crop, quality, format });
          break;
        case 'optimized':
        default:
          imageUrl = getOptimizedImageUrl(publicId, width || 800, height || 600);
          break;
      }
    } catch (error) {
      // If cloudinary config is not available, use publicId as-is (might be a Firebase Storage path)
      console.warn('Cloudinary config not available, using publicId as URL:', publicId);
      imageUrl = publicId;
    }
  }

  return (
    <div className={`relative ${className}`}>
      {!imageLoaded && !imageError && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ios-blue mx-auto mb-2"></div>
            <span className="text-gray-500 text-xs">Laden...</span>
          </div>
        </div>
      )}
      
      {imageError && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-gray-500 text-xl">🏠</span>
            </div>
            <span className="text-gray-500 text-xs">Foto niet gevonden</span>
            <div className="text-xs text-gray-400 mt-1">
              ID: {publicId?.substring(0, 20)}...
            </div>
          </div>
        </div>
      )}
      
      <img
        src={imageUrl}
        alt={alt}
        className={`w-full h-full object-cover ${imageLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
        loading="lazy"
        onError={() => {
          console.log('Image failed to load:', {
            publicId,
            imageUrl
          });
          setImageError(true);
        }}
        onLoad={() => {
          console.log('Image loaded successfully:', publicId);
          setImageLoaded(true);
        }}
        {...props}
      />
    </div>
  );
};

export default CloudinaryImage;
